'use client';

import { ReactNode, useState } from 'react';
import Image from 'next/image';
import Button from './Button';
import { ContentProps } from '@/models/fragments';

interface FacilitiesTabsProps {
	title?: string;
	facilities?: ContentProps[];
}

interface FacilityBullet {
	id: string;
	content: ReactNode;
}

interface FacilityContent {
	title: string;
	address: string;
	tags: string[];
	bullets: FacilityBullet[];
}

export default function FacilitiesTabs({ title, facilities }: FacilitiesTabsProps) {
	const [activeIndex, setActiveIndex] = useState(0);

	const facilitiesData: FacilityContent[] = [
		{
			title: 'Milwaukee 6th Street',
			address: '6055 S. 6th Street',
			tags: ['General Storage', 'Freezer Storage', 'Refrigerated Storage'],
			bullets: [
				{
					id: 'm6-space',
					content: (
						<>
							<strong>250,000 square feet</strong> of flexible warehouse space for dry
							food, <strong>general</strong>, and <strong>fridge storage</strong>.
						</>
					),
				},
				{
					id: 'm6-access',
					content: (
						<>
							Convenient access to <strong>I-94</strong> and advanced
							material-handling equipment.
						</>
					),
				},
				{
					id: 'm6-loading',
					content: (
						<>
							Efficient <strong>loading, unloading, and distribution</strong> services
							using racked and bulk storage configurations.
						</>
					),
				},
				{
					id: 'm6-ambient',
					content: (
						<>
							<strong>Ambient and climate-controlled environments</strong> possible.
						</>
					),
				},
				{
					id: 'm6-security',
					content: (
						<>
							Equipped with <strong>24/7 surveillance</strong>, fire protection
							systems, and controlled access points.
						</>
					),
				},
				{
					id: 'm6-ideal',
					content: (
						<strong>
							An ideal choice for businesses requiring a reliable and strategically
							located logistics partner.
						</strong>
					),
				},
			],
		},
		{
			title: 'Bayview',
			address: '1977 S. Allis Street',
			tags: ['HazMat Storage'],
			bullets: [
				{
					id: 'bayview-compliant',
					content: (
						<>
							The only <strong>fully compliant HazMat warehouse</strong> in
							Southeastern Wisconsin.
						</>
					),
				},
				{
					id: 'bayview-storage',
					content: (
						<>
							Specialized HazMat storage for{' '}
							<strong>corrosives, flammables, combustibles</strong>, and other
							hazardous materials.
						</>
					),
				},
				{
					id: 'bayview-safety',
					content: (
						<>
							Equipped with <strong>foam sprinkler systems</strong>, alarm monitoring,
							underground recovery tanks, and secondary containment measures.
						</>
					),
				},
				{
					id: 'bayview-team',
					content: (
						<>
							<strong>Dedicated, highly trained personnel</strong> ensure both
							compliance and operational excellence.
						</>
					),
				},
				{
					id: 'bayview-choice',
					content: (
						<>
							The <strong>premier choice</strong> for businesses seeking reliable and
							secure storage of regulated products.
						</>
					),
				},
			],
		},
		{
			title: 'Waukesha',
			address: 'W229 N1492 Westwood Drive',
			tags: ['General Storage', 'Refrigerated Storage', 'Freezer Storage'],
			bullets: [
				{
					id: 'waukesha-spectrum',
					content: (
						<>
							<strong>Premier food-grade storage solution</strong> designed to
							accommodate full spectrum of products, from{' '}
							<strong>refrigerated</strong> and <strong>frozen</strong> goods to{' '}
							<strong>general storage</strong> items.
						</>
					),
				},
				{
					id: 'waukesha-capacity',
					content: (
						<>
							With over <strong>35,000 square feet</strong> dedicated to fridge
							storage, <strong>40,000 square feet</strong> set aside for freezer
							storage, and additional <strong>140,000 square feet</strong> of dry
							storage space, this <strong>one-stop shop</strong> seamlessly handles
							varied temperature requirements.
						</>
					),
				},
				{
					id: 'waukesha-equipment',
					content:
						'Equipped with advanced material-handling equipment, robust inventory management systems, and stringent safety and compliance measures, our Waukesha location provides streamlined receiving, warehousing, and distribution for diverse industries.',
				},
				{
					id: 'waukesha-choice',
					content:
						'Whether you are seeking a reliable partner to store bulk perishables or need flexible space for seasonal surges, this facility is designed to meet the most demanding logistics needs while ensuring product integrity every step of the way.',
				},
			],
		},
		{
			title: 'Milwaukee 20th Street',
			address: '3201 S. 20th Street',
			tags: ['General Storage'],
			bullets: [
				{
					id: 'm20-ftz',
					content: (
						<>
							The only <strong>FTZ-certified warehouse</strong> in Milwaukee.
						</>
					),
				},
				{
					id: 'm20-capabilities',
					content: (
						<>
							Offers specialized{' '}
							<strong>
								direct-to-consumer, pharmaceutical, retail, and drop-ship
							</strong>{' '}
							storage capabilities.
						</>
					),
				},
				{
					id: 'm20-d2c',
					content: (
						<>
							Its <strong>D2C focus</strong> makes it ideal solution for e-commerce
							and retail companies looking to streamline distribution and accelerate
							order fulfillment.
						</>
					),
				},
				{
					id: 'm20-brc',
					content: (
						<>
							<strong>BRC-compliant certification</strong> ensures top-tier handling
							and safety for sensitive products.
						</>
					),
				},
				{
					id: 'm20-supplychain',
					content: (
						<>
							From secure storage to <strong>optimized order processing</strong>,
							delivers comprehensive supply chain solution.
						</>
					),
				},
				{
					id: 'm20-partner',
					content: (
						<>
							A <strong>strategic warehouse partner</strong> in Milwaukee.
						</>
					),
				},
			],
		},
	];

	const active = facilitiesData[activeIndex];
	const activeCmsFacility = facilities?.find((facility) => facility.title === active.title);

	return (
		<div className="bg-white py-12 md:py-20">
			<div className="container">
				{/* Header */}
				<div className="mb-8">
					<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 ">
						{title ?? 'Our facilities to meet all your logistical needs.'}
					</h2>
					<p className="text-primary-500 font-medium text-xl">
						Select a facility to view more details.
					</p>
				</div>

				{/* Tabs */}
				<div className="border-b border-gray-200 mb-8 overflow-x-auto">
					<div className="flex gap-0 min-w-max">
						{facilitiesData.map((facility, index) => (
							<button
								type="button"
								key={facility.title}
								onClick={() => setActiveIndex(index)}
								className={`px-4 py-2 text-base font-normal whitespace-nowrap transition-colors border-b-2  ${
									activeIndex === index
										? 'border-primary-600 text-[#0A0A0A] font-semibold'
										: 'border-transparent text-[#0A0A0A] hover:text-gray-800 hover:border-gray-300'
								}`}
							>
								{facility.title}
							</button>
						))}
					</div>
				</div>

				{/* Active Facility Content */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
					{/* Left: Image */}
					<div className="relative w-full aspect-[4/3] md:aspect-auto md:h-full min-h-[280px] md:min-h-[420px] overflow-hidden bg-gray-100">
						{activeCmsFacility?.image?.url ? (
							<Image
								src={activeCmsFacility.image.url}
								alt={activeCmsFacility.image.title ?? active.title}
								fill
								className="object-cover"
								sizes="(max-width: 768px) 100vw, 50vw"
							/>
						) : (
							<div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
								No image available
							</div>
						)}
					</div>

					{/* Right: Details */}
					<div className="flex flex-col gap-4">
						{/* Tags */}
						{!!active.tags.length && (
							<div className="flex flex-wrap gap-2">
								{active.tags.map((tag) => (
									<span
										key={tag}
										className="text-[12px] font-semibold px-3 py-1 rounded-full bg-[#D1E1F9] text-[#1A335B]"
									>
										{tag}
									</span>
								))}
							</div>
						)}

						{/* Title */}
						<h3 className="text-3xl font-bold text-gray-900">{active.title}</h3>

						{/* Address */}
						<a
							href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(active.address)}`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-500 underline hover:no-underline font-semibold text-base cursor-pointer"
						>
							{active.address}
						</a>

						{/* Description bullets */}
						<ul className="space-y-2">
							{active.bullets.map((bullet) => (
								<li
									key={bullet.id}
									className="flex items-start gap-2 text-base text-[#0A0A0A] leading-6"
								>
									<span className="mt-2 h-3 w-3 shrink-0 rounded-full bg-primary-500" />
									<span>{bullet.content}</span>
								</li>
							))}
						</ul>

						{/* Buttons */}
						<div className="flex flex-wrap gap-3 mt-2">
							<Button href="/contact" color="dark" variant="contained">
								Inquire about storage
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
